import type { ConsentChallengeResponse, ConsentSource, ConsentState, PrivacyPublication } from './types';
import { getVisitorUid } from './visitor-uid';
import { matchesPublication, publicationIsCurrent, publicationReference } from './publication';

let API_BASE = 'https://crm.emergeitalia.it';
const CHALLENGE_TIMEOUT_MS = 1_500;

export function configureApiBase(base: string): void {
  API_BASE = base.replace(/\/$/, '');
}

export function getApiBase(): string {
  return API_BASE;
}

async function requestConsentChallenge(tid: string, bannerVersion: string): Promise<string | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CHALLENGE_TIMEOUT_MS);
  try {
    const response = await fetch(
      `${API_BASE}/api/policy/${encodeURIComponent(tid)}/consent/challenge`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ banner_version: bannerVersion }),
        keepalive: true,
        signal: controller.signal,
      },
    );
    if (!response.ok) return null;
    const body = (await response.json().catch(() => null)) as ConsentChallengeResponse | null;
    return typeof body?.challenge === 'string' && body.challenge.length > 0 ? body.challenge : null;
  } catch {
    // Compatibilità: legacy e optional devono poter registrare anche se il
    // nuovo endpoint non è ancora disponibile o la rete è lenta.
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export async function postConsent(
  tid: string | null,
  state: ConsentState,
  source: ConsentSource,
): Promise<void> {
  if (!tid) {
    console.warn(
      '[EmergePrivacy] data-tid assente: la scelta resta nel browser e non viene registrata. Il consenso non è dimostrabile.',
    );
    return;
  }

  const challenge = await requestConsentChallenge(tid, state.version);
  try {
    const res = await fetch(`${API_BASE}/api/policy/${encodeURIComponent(tid)}/consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitor_uid: getVisitorUid(),
        categories: state.categories,
        source,
        consent_version: state.version,
        ...(challenge ? { challenge } : {}),
      }),
      keepalive: true,
    });
    // La registrazione resta best-effort — non deve rompere la pagina — ma non
    // può restare invisibile: finora un data-tid sbagliato o un dominio non
    // registrato producevano un errore HTTP che nessuno vedeva, e il sito
    // sembrava conforme mentre non registrava nulla.
    if (!res.ok) {
      console.warn(
        `[EmergePrivacy] consenso NON registrato (HTTP ${res.status}). Verifica che data-tid sia policy_sites.id e che il dominio sia registrato nel CRM.`,
      );
    }
  } catch (err) {
    console.warn('[EmergePrivacy] consenso NON registrato (rete):', err);
  }
}

/** Modalità forte: nessun fallback legacy, identità verificata su challenge e ACK. */
export async function postReleaseConsent(
  tid: string,
  state: ConsentState,
  source: ConsentSource,
  publication: PrivacyPublication,
  signal?: AbortSignal,
): Promise<{ ok: boolean; error?: string }> {
  if (signal?.aborted) return { ok: false, error: 'consent_superseded' };
  if (publication.site_id !== tid || !publicationIsCurrent(publication)) return { ok: false, error: 'publication_expired_or_mismatch' };
  const { site_id: _siteId, ...reference } = publicationReference(publication);
  const endpoint = `${API_BASE}/api/policy/${encodeURIComponent(tid)}/consent`;
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    const challengeResponse = await fetch(`${endpoint}/challenge`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ banner_version: publication.banner_version, ...reference }),
      signal: controller.signal, keepalive: true,
    });
    if (!challengeResponse.ok) return { ok: false, error: `challenge_http_${challengeResponse.status}` };
    const challenge = await challengeResponse.json();
    if (controller.signal.aborted) return { ok: false, error: 'consent_superseded' };
    if (!matchesPublication(challenge, publication) || typeof challenge.challenge !== 'string' || !challenge.challenge
        || (challenge.expires_at !== undefined && !(Date.parse(challenge.expires_at) > Date.now()))) {
      return { ok: false, error: 'challenge_mismatch_or_expired' };
    }
    const body = JSON.stringify({
      visitor_uid: getVisitorUid(), categories: state.categories, source,
      consent_version: publication.banner_version, challenge: challenge.challenge,
      ...reference, idempotency_key: crypto.randomUUID(),
    });
    const send = () => fetch(endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body,
      signal: controller.signal, keepalive: true,
    });
    let response: Response;
    try { response = await send(); } catch (error) {
      if (controller.signal.aborted) throw error;
      // Un solo retry di trasporto, stessa chiave e stesso payload: nessun nuovo evento.
      response = await send();
    }
    if (!response.ok) return { ok: false, error: `consent_http_${response.status}` };
    const acknowledgement = await response.json();
    if (controller.signal.aborted) return { ok: false, error: 'consent_superseded' };
    if (acknowledgement.ok !== true || typeof acknowledgement.id !== 'string' || !acknowledgement.id
        || acknowledgement.ingest_assurance !== 'release_challenge'
        || !matchesPublication(acknowledgement, publication) || !publicationIsCurrent(publication)) {
      return { ok: false, error: 'consent_acknowledgement_mismatch' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'consent_network_error' };
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}

/**
 * GDPR Art. 17 — Cancella server-side il consent record + observation
 * inventory associate al visitor_uid. Best-effort.
 */
export async function deleteConsentServer(
  tid: string | null,
  visitorUid: string,
): Promise<{ ok: boolean; consent_records_deleted?: number; observations_deleted?: number; error?: string }> {
  if (!tid) return { ok: false, error: 'missing tid' };
  try {
    const url = `${API_BASE}/api/policy/${encodeURIComponent(tid)}/consent?visitor_uid=${encodeURIComponent(visitorUid)}`;
    const res = await fetch(url, { method: 'DELETE', keepalive: true });
    if (!res.ok) {
      return { ok: false, error: `HTTP ${res.status}` };
    }
    const json = (await res.json().catch(() => ({}))) as {
      consent_records_deleted?: number;
      observations_deleted?: number;
    };
    return {
      ok: true,
      consent_records_deleted: json.consent_records_deleted,
      observations_deleted: json.observations_deleted,
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}
